import React, {
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
  useEffect,
} from 'react';

// The window `resize` event fires continuously while the user drags a window
// edge -- dozens of times a second. This provider wraps <App/> (see index.js),
// so publishing a new size object on every one of those ticks re-rendered
// every consumer, and because App itself consumed the raw width that meant
// re-rendering the entire app tree: the presentation editor, its 80 <SlideEdit>
// children, the Sidebar and any open modal, all at resize frequency. That is
// what made resizing the window while the Dynamic Hotel Slide Library was open
// lock up.
//
// Two things fix it, and both matter:
//
//  1. Coalesce. Resize ticks are batched into one update per animation frame,
//     so the work is bounded by the frame rate rather than by how fast the OS
//     emits events, and an update that doesn't change the numbers is dropped
//     entirely (object identity is preserved so consumers don't re-render).
//
//  2. Quantise. Nearly every consumer only ever asks which side of a
//     breakpoint it is on (`width < 576`, `width >= 992`), not for the live
//     pixel value. Those consumers subscribe to `useBreakpointWidth()`, which
//     republishes only when the width crosses a breakpoint -- so they re-render
//     on the order of once per resize gesture instead of once per frame.
//
// Reach for `useWindowSize()` only when you genuinely need the live pixel
// value, and prefer a CSS media query or `calc()` over either hook when the
// value is only ever fed back into styles.

// Every breakpoint the app compares a window width against. A superset is
// always safe -- an extra entry only means an extra (cheap, rare) republish --
// but a MISSING one is a correctness bug: consumers either side of it would be
// handed a width from the wrong side of their own comparison. Add to this list
// when you introduce a new width comparison.
const BREAKPOINTS = [375, 576, 768, 992, 993, 1200, 1291, 1400, 1467];

// Which "cell" of the breakpoint line a width falls in. Cells alternate
// between the open gaps BETWEEN breakpoints and the breakpoints themselves, so
// an exact hit (width === 992) is its own cell. That is what makes the
// quantisation sound: any two widths sharing a cell give the same answer for
// `<`, `<=`, `>`, `>=` and `===` against every breakpoint in the list, so a
// consumer handed either one renders identically. Were exact hits folded into
// the gap above them, `width > 576` would disagree at exactly 576.
const cellOf = (width) => {
  let i = 0;
  while (i < BREAKPOINTS.length && BREAKPOINTS[i] < width) i++;
  return i < BREAKPOINTS.length && BREAKPOINTS[i] === width ? i * 2 + 1 : i * 2;
};

const readWindow = () => ({
  width: window.innerWidth,
  height: window.innerHeight,
});

// Live size, one update per frame at most.
const WindowSizeContext = createContext(readWindow());
// Breakpoint-stable width, republished only on a breakpoint crossing. The
// default has to carry the same `{ width }` shape the provider supplies, so a
// consumer rendered outside the provider still destructures to a real number
// rather than undefined.
const BreakpointWidthContext = createContext({ width: window.innerWidth });

export const WindowSizeProvider = ({ children }) => {
  const [size, setSize] = useState(readWindow);

  // The width last published to breakpoint consumers. It trails `size.width`
  // between crossings, and is deliberately a real measured width rather than
  // the breakpoint itself so that `===` comparisons still behave.
  const [breakpointWidth, setBreakpointWidth] = useState(size.width);

  // Read inside the resize handler without making it a dependency, so the
  // listener is attached once for the life of the provider.
  const sizeRef = useRef(size);
  const cellRef = useRef(cellOf(size.width));

  useEffect(() => {
    let frame = null;

    const measure = () => {
      frame = null;
      const next = readWindow();
      const prev = sizeRef.current;

      // A resize that ends where it started -- or a tick that fires without the
      // viewport actually moving, which happens when the on-screen keyboard
      // opens on mobile -- must not invalidate the context value.
      if (next.width === prev.width && next.height === prev.height) return;

      sizeRef.current = next;
      setSize(next);

      const nextCell = cellOf(next.width);
      if (nextCell !== cellRef.current) {
        cellRef.current = nextCell;
        setBreakpointWidth(next.width);
      }
    };

    // Collapse every tick that arrives within the same frame into one measure.
    // Reading the size in the rAF callback rather than in the event also keeps
    // the layout read off the event handler's critical path.
    const handleResize = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(measure);
    };

    // The viewport may already have moved between module evaluation and this
    // effect running, so reconcile once on mount.
    measure();

    window.addEventListener('resize', handleResize);

    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // `size` is already a fresh object per real change, so it needs no memo of
  // its own; this only keeps the breakpoint value's shape stable.
  const breakpointValue = useMemo(
    () => ({ width: breakpointWidth }),
    [breakpointWidth],
  );

  return (
    <WindowSizeContext.Provider value={size}>
      <BreakpointWidthContext.Provider value={breakpointValue}>
        {children}
      </BreakpointWidthContext.Provider>
    </WindowSizeContext.Provider>
  );
};

/**
 * Live window size, updated at most once per animation frame.
 *
 * Only use this if you need the actual pixel value as it changes. If you are
 * comparing against a breakpoint, use `useBreakpointWidth` instead -- this hook
 * will re-render your component for the whole duration of a resize drag.
 *
 * @returns {{width: number, height: number}}
 */
export const useWindowSize = () => {
  return useContext(WindowSizeContext);
};

/**
 * Window width for breakpoint comparisons, republished only when the width
 * crosses one of the breakpoints in `BREAKPOINTS`.
 *
 * Safe for any `<`, `<=`, `>`, `>=` or `===` comparison against a listed
 * breakpoint; it is NOT the live width, so don't feed it into a pixel
 * calculation or render it.
 *
 * @returns {{width: number}}
 */
export const useBreakpointWidth = () => {
  return useContext(BreakpointWidthContext);
};
